import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/landing/Navbar';
import { Footer } from '../components/landing/Footer';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen text-[rgb(var(--fg))] relative overflow-hidden bg-black">
      <div className="relative z-10">
        <Navbar />
        
        <main className="max-w-4xl mx-auto px-4 md:px-8 py-16 mt-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div className="mb-12">
              <Link to="/home" className="inline-flex items-center mb-6 text-neutral-400 hover:text-[rgb(var(--fg))] transition-colors">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Home
              </Link>
              <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Privacy Policy
              </h1>
              <p className="text-neutral-400 text-lg">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-invert prose-lg max-w-none space-y-8">
              <section>
                <p className="text-neutral-300 leading-relaxed">
                  At FiverFlow, we are committed to protecting your privacy and ensuring the security of your 
                  personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard 
                  your information when you use our website, applications, and services (collectively, the "Service"). 
                  Please read this Privacy Policy carefully. By using our Service, you agree to the collection and 
                  use of information in accordance with this policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">1. Information We Collect</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-[rgb(var(--fg))] mt-6 mb-3">1.1 Information You Provide</h3>
                    <p className="text-neutral-300 leading-relaxed mb-2">
                      We collect information that you voluntarily provide when you:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-neutral-300 ml-4">
                      <li>Create an account (name, email address, username, password)</li>
                      <li>Update your profile information</li>
                      <li>Subscribe to a paid plan (payment information processed by third-party payment processors)</li>
                      <li>Contact us for support or inquiries</li>
                      <li>Participate in surveys or promotional activities</li>
                      <li>Upload content, including invoices, client information, and business data</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-[rgb(var(--fg))] mt-6 mb-3">1.2 Automatically Collected Information</h3>
                    <p className="text-neutral-300 leading-relaxed mb-2">
                      When you use our Service, we automatically collect certain information about your device and usage:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-neutral-300 ml-4">
                      <li>Device information (IP address, browser type, operating system)</li>
                      <li>Usage data (pages visited, features used, time spent on the Service)</li>
                      <li>Log data (access times, error logs, performance data)</li>
                      <li>Cookies and similar tracking technologies (see our Cookie Policy for details)</li>
                      <li>Location data (if you grant permission)</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-[rgb(var(--fg))] mt-6 mb-3">1.3 Information from Third Parties</h3>
                    <p className="text-neutral-300 leading-relaxed">
                      We may receive information about you from third-party services, such as authentication providers 
                      (e.g., Google, Discord), payment processors, and analytics services. This information is used 
                      to provide and improve our Service.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">2. How We Use Your Information</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    We use the information we collect for the following purposes:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-300 ml-4">
                    <li><strong>Service Delivery:</strong> To provide, maintain, and improve our Service, including processing transactions and managing your account</li>
                    <li><strong>Communication:</strong> To send you service-related notifications, updates, and administrative messages</li>
                    <li><strong>Customer Support:</strong> To respond to your inquiries, provide technical support, and resolve issues</li>
                    <li><strong>Personalization:</strong> To customize your experience and provide personalized content and features</li>
                    <li><strong>Analytics:</strong> To analyze usage patterns, identify trends, and improve our Service</li>
                    <li><strong>Security:</strong> To detect, prevent, and address security issues, fraud, and abuse</li>
                    <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
                    <li><strong>Marketing:</strong> To send you promotional communications (with your consent, where required by law)</li>
                    <li><strong>Business Operations:</strong> To conduct business analytics, research, and development</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">3. How We Share Your Information</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    We do not sell your personal information. We may share your information in the following circumstances:
                  </p>

                  <div>
                    <h3 className="text-xl font-semibold text-[rgb(var(--fg))] mt-6 mb-3">3.1 Service Providers</h3>
                    <p className="text-neutral-300 leading-relaxed">
                      We may share information with third-party service providers who perform services on our behalf, 
                      such as hosting, payment processing, analytics, email delivery, and customer support. These 
                      providers are contractually obligated to protect your information and use it only for the purposes 
                      we specify.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-[rgb(var(--fg))] mt-6 mb-3">3.2 Legal Requirements</h3>
                    <p className="text-neutral-300 leading-relaxed">
                      We may disclose your information if required by law, regulation, legal process, or governmental 
                      request, or to protect our rights, property, or safety, or that of our users or others.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-[rgb(var(--fg))] mt-6 mb-3">3.3 Business Transfers</h3>
                    <p className="text-neutral-300 leading-relaxed">
                      In the event of a merger, acquisition, reorganization, or sale of assets, your information may 
                      be transferred to the acquiring entity. We will notify you of any such change in ownership.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-[rgb(var(--fg))] mt-6 mb-3">3.4 With Your Consent</h3>
                    <p className="text-neutral-300 leading-relaxed">
                      We may share your information with third parties when you explicitly consent to such sharing.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">4. Data Security</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    We implement appropriate technical and organizational security measures to protect your personal 
                    information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-300 ml-4">
                    <li>Encryption of data in transit and at rest</li>
                    <li>Regular security assessments and updates</li>
                    <li>Access controls and authentication mechanisms</li>
                    <li>Secure data storage and backup systems</li>
                    <li>Employee training on data protection</li>
                  </ul>
                  <p className="text-neutral-300 leading-relaxed">
                    However, no method of transmission over the Internet or electronic storage is 100% secure. While 
                    we strive to use commercially acceptable means to protect your information, we cannot guarantee 
                    absolute security. You are responsible for maintaining the confidentiality of your account credentials.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">5. Your Rights and Choices</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    Depending on your location, you may have certain rights regarding your personal information under 
                    applicable privacy laws, including the Personal Information Protection and Electronic Documents Act 
                    (PIPEDA) in Canada. These rights may include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-300 ml-4">
                    <li><strong>Access:</strong> The right to access and receive a copy of your personal information</li>
                    <li><strong>Correction:</strong> The right to correct inaccurate or incomplete information</li>
                    <li><strong>Deletion:</strong> The right to request deletion of your personal information</li>
                    <li><strong>Objection:</strong> The right to object to processing of your personal information</li>
                    <li><strong>Restriction:</strong> The right to request restriction of processing</li>
                    <li><strong>Portability:</strong> The right to receive your data in a structured, machine-readable format</li>
                    <li><strong>Withdraw Consent:</strong> The right to withdraw consent where processing is based on consent</li>
                  </ul>
                  <p className="text-neutral-300 leading-relaxed">
                    To exercise these rights, please contact us at privacy@fiverflow.com. We will respond to your 
                    request within a reasonable timeframe and in accordance with applicable law.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">6. Data Retention</h2>
                <p className="text-neutral-300 leading-relaxed">
                  We retain your personal information only for as long as necessary to fulfill the purposes outlined 
                  in this Privacy Policy, unless a longer retention period is required or permitted by law. When we 
                  no longer need your information, we will securely delete or anonymize it. Account data is typically 
                  retained until you request deletion or your account is inactive for an extended period.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">7. Children's Privacy</h2>
                <p className="text-neutral-300 leading-relaxed">
                  Our Service is not intended for individuals under the age of 18. We do not knowingly collect 
                  personal information from children. If we become aware that we have collected information from a 
                  child without parental consent, we will take steps to delete such information. If you believe we 
                  have collected information from a child, please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">8. International Data Transfers</h2>
                <p className="text-neutral-300 leading-relaxed">
                  Your information may be transferred to and processed in countries other than your country of 
                  residence. These countries may have data protection laws that differ from those in your country. 
                  We take appropriate safeguards to ensure that your information receives an adequate level of 
                  protection, including through contractual agreements with service providers.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">9. Cookies and Tracking Technologies</h2>
                <p className="text-neutral-300 leading-relaxed">
                  We use cookies and similar tracking technologies to collect and store information about your use 
                  of our Service. For detailed information about our use of cookies and how to manage them, please 
                  see our <Link to="/cookie-policy" className="text-indigo-400 hover:text-indigo-300">Cookie Policy</Link>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">10. Third-Party Links</h2>
                <p className="text-neutral-300 leading-relaxed">
                  Our Service may contain links to third-party websites or services. We are not responsible for the 
                  privacy practices of these third parties. We encourage you to read the privacy policies of any 
                  third-party sites you visit.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">11. Changes to This Privacy Policy</h2>
                <p className="text-neutral-300 leading-relaxed">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or for 
                  legal, operational, or regulatory reasons. We will notify you of any material changes by posting 
                  the new Privacy Policy on this page and updating the "Last updated" date. We may also notify you 
                  via email or through a prominent notice on our Service. Your continued use of the Service after 
                  such changes constitutes your acceptance of the updated Privacy Policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">12. Contact Us</h2>
                <p className="text-neutral-300 leading-relaxed mb-4">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy 
                  practices, please contact us:
                </p>
                <div className="mt-4 p-4 bg-neutral-900 rounded-lg border border-white/10">
                  <p className="text-neutral-300">
                    <strong>FiverFlow Privacy Team</strong><br />
                    Email: privacy@fiverflow.com<br />
                    Website: <Link to="/home" className="text-indigo-400 hover:text-indigo-300">https://fiverflow.com</Link>
                  </p>
                </div>
                <p className="text-neutral-300 leading-relaxed mt-4">
                  We will respond to your inquiry within a reasonable timeframe and in accordance with applicable 
                  privacy laws.
                </p>
              </section>
            </div>
          </motion.div>
        </main>

        <Footer />
      </div>
    </div>
  );
}

